import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { getJobActivity } from "../../../lib/api";
import { formatAttachmentDate } from "../lib/attachmentKind";
import type { ActivityChange, ActivityEntry } from "../types";
import form from "./NewJobForm.module.css";
import styles from "./ActivityPage.module.css";

/** Page size for keyset pagination; mirrors the server's default limit. */
const PAGE_SIZE = 50;

/** Badge text per event type (deliberately plain text, no glyphs). */
const EVENT_BADGES: Record<ActivityEntry["event"], string> = {
  created: "Created",
  updated: "Edited",
  moved: "Moved",
  reordered: "Reordered",
  attachment_added: "File added",
  attachment_removed: "File removed",
};

/**
 * Visual weight per event type. Colour is never the only signal — the badge
 * always carries its own words, and the rail dot only repeats what it says.
 */
const EVENT_TONES: Record<ActivityEntry["event"], "accent" | "neutral" | "danger"> = {
  created: "accent",
  updated: "neutral",
  moved: "accent",
  reordered: "neutral",
  attachment_added: "neutral",
  attachment_removed: "danger",
};

/** Human labels for machine field keys (mirrors the server's summary map). */
const FIELD_LABELS: Record<string, string> = {
  clientName: "Client",
  clientContact: "Contact",
  propertyAddress: "Property address",
  propertyConfirmed: "Property confirmed",
  description: "Description",
  priority: "Priority",
  reportedBy: "Reported by",
  accessNotes: "Access notes",
  occupantName: "Occupant",
  occupantPhone: "Occupant phone",
  requireQuoteApproval: "Quote approval required",
  refExternal: "External ref",
  priceEstimate: "Price estimate",
  "timing.requirement": "Timing requirement",
  "timing.requestedCompletionOn": "Requested completion",
  "timing.requestedNotBeforeOn": "Not before",
  "timing.reason": "Timing reason",
  accessWindows: "Access windows",
  scopeDescription: "Scope description",
  scopeTaxRate: "GST rate",
  column: "Column",
};

/** Fields the feed treats as headline changes (bold in the expanded diff). */
const SIGNIFICANT_FIELDS = new Set([
  "column",
  "description",
  "priority",
  "priceEstimate",
  "clientName",
  "clientContact",
  "propertyAddress",
  "scopeDescription",
  "scopeTaxRate",
  "timing.requirement",
  "timing.requestedCompletionOn",
  "timing.requestedNotBeforeOn",
]);

const isSignificant = (change: ActivityChange): boolean =>
  SIGNIFICANT_FIELDS.has(change.field) || change.field.startsWith("lineItem:");

const changeLabel = (change: ActivityChange): string =>
  change.label ?? FIELD_LABELS[change.field] ?? change.field;

const parseDate = (iso: string): Date | null => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "11:41 pm" — the day is already named by the group heading above the row. */
const timeOfDay = (iso: string): string => {
  const date = parseDate(iso);
  if (!date) return "Unknown";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const sameDay = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();

/**
 * Day heading text. "Today"/"Yesterday" lead because a run of edits is read
 * relative to now, but the calendar date always follows: an audit trail that
 * only says "yesterday" is not one you can quote in a dispute.
 */
function dayLabel(iso: string): string {
  const date = parseDate(iso);
  if (!date) return "Unknown date";
  const absolute = date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, now)) return `Today · ${absolute}`;
  if (sameDay(date, yesterday)) return `Yesterday · ${absolute}`;
  return absolute;
}

const dayKey = (iso: string): string => parseDate(iso)?.toDateString() ?? "unknown";

interface DayGroup {
  key: string;
  label: string;
  entries: ActivityEntry[];
}

export interface ActivityPageProps {
  /** Persisted job id — this page only renders in edit mode. */
  jobId: string;
}

/**
 * The Activity page — the card's append-only audit trail (card-activity epic).
 *
 * Server data, not draft fields: entries load from
 * `GET /api/jobs/:id/activity` (newest first, 50 per page with a
 * load-older keyset cursor). Each row is the server-rendered one-liner;
 * a disclosure expands the per-field diff stored on `updated` events. Vendor
 * callers only — the API refuses client-scoped callers.
 *
 * The feed is a dated timeline rather than a flat list: entries are grouped
 * under the day they happened on, so the date is printed once per day and each
 * row only carries its time. Every row still exposes the full timestamp through
 * its `<time>` element for assistive technology and hover.
 */
export function ActivityPage({ jobId }: ActivityPageProps) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Bumped by Retry; re-runs the initial load without remounting the page.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getJobActivity(jobId)
      .then((result) => {
        if (!cancelled) setEntries(result.activity);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Could not load activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, reloadToken]);

  const loadOlder = useCallback((): void => {
    const last = entries[entries.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    getJobActivity(jobId, last.createdAt)
      .then((result) => {
        setEntries((previous) => [...previous, ...result.activity]);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load older activity");
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [entries, jobId, loadingMore]);

  // Entries arrive newest-first, so a day's rows are already adjacent and the
  // groups stay in order as older pages are appended.
  const groups = useMemo<DayGroup[]>(() => {
    const result: DayGroup[] = [];
    for (const entry of entries) {
      const key = dayKey(entry.createdAt);
      const current = result[result.length - 1];
      if (current && current.key === key) current.entries.push(entry);
      else result.push({ key, label: dayLabel(entry.createdAt), entries: [entry] });
    }
    return result;
  }, [entries]);

  const hasMore = !loading && !error && entries.length >= PAGE_SIZE;
  const toggle = (id: string): void =>
    setExpandedId((current) => (current === id ? null : id));

  return (
    <section className={`${form.section} ${styles.root}`}>
      <h3 className={form.sectionTitle}>Activity</h3>

      {error && (
        <p className={styles.error} role="alert">
          <span className={styles.errorText}>{error}</span>
          <button
            type="button"
            className={styles.retry}
            onClick={() => setReloadToken((token) => token + 1)}
          >
            Retry
          </button>
        </p>
      )}

      {loading && (
        <div className={styles.skeleton} role="status" aria-label="Loading activity">
          <span className={styles.skeletonRow} />
          <span className={styles.skeletonRow} />
          <span className={styles.skeletonRow} />
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <p className={styles.empty}>No activity yet — changes to this card will appear here.</p>
      )}

      {!loading && groups.length > 0 && (
        <div className={styles.feed}>
          {groups.map((group) => (
            <section key={group.key} className={styles.day}>
              <h4 className={styles.dayLabel}>{group.label}</h4>
              <ol className={styles.list}>
                {group.entries.map((entry) => (
                  <ActivityRow
                    key={entry.id}
                    entry={entry}
                    expanded={expandedId === entry.id}
                    onToggle={() => toggle(entry.id)}
                  />
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}

      {hasMore && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.more}
            onClick={loadOlder}
            disabled={loadingMore}
            aria-busy={loadingMore || undefined}
          >
            {loadingMore ? "Loading…" : "Load older activity"}
          </button>
        </div>
      )}
    </section>
  );
}

interface ActivityRowProps {
  entry: ActivityEntry;
  expanded: boolean;
  onToggle: () => void;
}

/** One event: badge, summary, who and when, and an optional per-field diff. */
function ActivityRow({ entry, expanded, onToggle }: ActivityRowProps) {
  const panelId = useId();
  const changes = entry.changes ?? [];
  const tone = EVENT_TONES[entry.event] ?? "neutral";
  const badgeClass =
    tone === "accent" ? styles.badgeAccent : tone === "danger" ? styles.badgeDanger : styles.badge;

  return (
    <li className={styles.item} data-tone={tone}>
      <div className={styles.itemHead}>
        <span className={badgeClass}>{EVENT_BADGES[entry.event] ?? "Event"}</span>
        <p className={styles.summary}>{entry.summary}</p>
      </div>

      <div className={styles.meta}>
        <span className={styles.actor}>{entry.actor}</span>
        <span className={styles.sep} aria-hidden="true">
          ·
        </span>
        <time className={styles.time} dateTime={entry.createdAt} title={formatAttachmentDate(entry.createdAt)}>
          {timeOfDay(entry.createdAt)}
        </time>
        {changes.length > 0 && (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggle}
          >
            <span className={styles.caret} aria-hidden="true">
              <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
                <path
                  d="M4 2.5 8 6l-4 3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {expanded ? "Hide changes" : `${changes.length} change${changes.length === 1 ? "" : "s"}`}
          </button>
        )}
      </div>

      {expanded && (
        <dl className={styles.diff} id={panelId}>
          {changes.map((change) => (
            <div
              key={`${entry.id}:${change.field}`}
              className={isSignificant(change) ? styles.diffRowSignificant : styles.diffRow}
            >
              <dt className={styles.diffLabel}>{changeLabel(change)}</dt>
              <dd className={styles.diffValues}>
                {/* <del>/<ins>: a before-and-after pair is what these elements
                    are for, so the direction survives without the arrow. */}
                <del className={styles.diffFrom}>{change.from ?? "—"}</del>
                <span className={styles.diffArrow} aria-hidden="true">
                  →
                </span>
                <ins className={styles.diffTo}>{change.to ?? "—"}</ins>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}
